/* eslint-disable react/no-array-index-key */
/* eslint-disable react/destructuring-assignment */
import { CustomSankeyLayerProps, DefaultLink, DefaultNode } from '@nivo/sankey';
import * as d3 from 'd3-shape';
import React from 'react';
import { CustomSankeyNode } from './CustomSankeyNodes';
import { Coords } from './makeCustomSankey';

type CustomSankeyLinksProps = {
  customNodes: Record<string, CustomSankeyNode>;
  nodeHover: string;
  hover: string;
  setHover: React.Dispatch<React.SetStateAction<string>>;
  setCoords: React.Dispatch<React.SetStateAction<Coords | null>>;
};

const CustomSankeyLinks: React.FC<
  CustomSankeyLayerProps<DefaultNode, DefaultLink> & CustomSankeyLinksProps
> = (props) => {
  const { links, customNodes, nodeHover, hover, setHover, setCoords } = props;

  const linkCurveBiases = [
    { source: 'Pending', target: 'Escalate 1', points: [{ y: 30 }] },
    { source: 'Pending', target: 'Acceptable', points: [{ y: 0 }] },
    { source: 'Escalate', target: 'Acceptable', points: [{ y: 0 }] },
    { source: 'Escalate 1', target: 'Acceptable', points: [{ y: 0 }] },
  ];

  const linksData = links
    .map((link) => ({
      source: link.source.id,
      target: link.target.id,
      value: link.value,
    }))
    .map((link, _idx, arr) => {
      const startLinks = arr.filter((l) => l.source === link.source);
      const totalStartValue = startLinks.reduce((acc, cur) => cur.value + acc, 0);
      const endLinks = arr.filter((l) => l.target === link.target);
      const totalEndValue = endLinks.reduce((acc, cur) => cur.value + acc, 0);

      const sourceNode = customNodes[link.source];
      const targetNode = customNodes[link.target];

      const startThickness = (sourceNode.height - 20) * (link.value / totalStartValue);
      const endThickness = (targetNode.height - 20) * (link.value / totalEndValue);
      const thickness = startThickness < endThickness ? startThickness : endThickness;

      return {
        ...link,
        thickness,
      };
    });

  const data: {
    points: { x: number; y: number; thickness: number }[];
    source: string;
    target: string;
  }[] = linksData.map((link, _idx, arr) => {
    const startLinks = arr.filter((l) => l.source === link.source);
    const endLinks = arr.filter((l) => l.target === link.target);
    const totalStartThickness = startLinks.reduce((acc, cur) => cur.thickness + acc, 0);
    const totalEndThickness = endLinks.reduce((acc, cur) => cur.thickness + acc, 0);

    const sourceNode = customNodes[link.source];
    const targetNode = customNodes[link.target];

    let startPointY = sourceNode.y + sourceNode.height / 2 - totalStartThickness / 2;
    for (let i = 0; i < startLinks.length; i += 1) {
      if (startLinks[i].source === link.source && startLinks[i].target === link.target) {
        break;
      }
      startPointY += startLinks[i].thickness;
    }

    let endPointY = targetNode.y + targetNode.height / 2 - totalEndThickness / 2;
    for (let i = 0; i < endLinks.length; i += 1) {
      if (endLinks[i].source === link.source && endLinks[i].target === link.target) {
        break;
      }
      endPointY += endLinks[i].thickness;
    }

    const startPoint = {
      x: sourceNode.x + sourceNode.width,
      y: startPointY,
      thickness: link.thickness,
    };
    const endPoint = { x: targetNode.x, y: endPointY, thickness: link.thickness };

    let biasPoints: { x: number; y: number; thickness: number }[] = [];
    const linkBiases = linkCurveBiases.find(
      (bias) => link.source === bias.source && link.target === bias.target
    );

    if (linkBiases) {
      biasPoints = linkBiases.points.map((bias, idx, biasArr) => ({
        x: startPoint.x + ((idx + 1) * (endPoint.x - startPoint.x)) / (biasArr.length + 1),
        y: endPoint.y + bias.y,
        thickness: link.thickness,
      }));
    }

    const points = [startPoint, ...biasPoints, endPoint];
    // console.log(biasPoints, points);

    return {
      points,
      source: link.source,
      target: link.target,
    };
  });

  const areaGenerator = d3
    .area<{ x: number; y: number; thickness: number }>()
    .x((d) => d.x)
    .y1((d) => d.y)
    .y0((d) => d.y + d.thickness)
    .curve(d3.curveBumpX);

  const handleMouseEnter = (event: React.MouseEvent<Element>, id: string): void => {
    setCoords({
      left: event.pageX,
      top: event.pageY,
    });
    setHover(id);
  };

  return (
    <g className="relative">
      {data
        .filter((link) => `${link.source}-${link.target}` !== hover)
        .map((link) => {
          const id = `${link.source}--${link.target}`;

          return (
            <path
              key={id}
              onMouseEnter={(e): void => {
                handleMouseEnter(e, id);
              }}
              onMouseLeave={(): void => {
                setHover('');
              }}
              onMouseMove={(e): void => {
                handleMouseEnter(e, id);
              }}
              d={`${areaGenerator(link.points)}`}
              fill="currentColor"
              className={`cursor-pointer  ${
                link.source === nodeHover || link.target === nodeHover
                  ? 'text-litlingo-complementary-yellow'
                  : 'opacity-70 text-litlingo-gray-2'
              }`}
            />
          );
        })}
      {data
        .filter((link) => `${link.source}--${link.target}` === hover)
        .map((link) => {
          const id = `${link.source}--${link.target}`;

          return (
            <path
              key={id}
              onMouseEnter={(e): void => {
                handleMouseEnter(e, id);
              }}
              onMouseLeave={(): void => {
                setHover('');
              }}
              onMouseMove={(e): void => {
                handleMouseEnter(e, id);
              }}
              d={`${areaGenerator(link.points)}`}
              fill="currentColor"
              className="text-litlingo-complementary-yellow cursor-pointer"
            />
          );
        })}
    </g>
  );
};

export default CustomSankeyLinks;
